import React from "react";
import { useUser } from "@/contexts/user-provider";
import type { CalendarTextSize } from "@/types/provider";

/**
 * How much each Calendar text size scales event text. The calendar-* font
 * sizes in tailwind.config multiply by --calendar-font-scale, so every event
 * card inside an element carrying {@link calendarTextScaleStyle} follows it.
 */
export const CALENDAR_TEXT_SCALE: Record<CalendarTextSize, number> = {
  // Small is the calendar's original look, and where everyone starts.
  small: 1,
  medium: 1.2,
  large: 1.4,
  extra_large: 1.6
};
export const CALENDAR_TEXT_SIZE_OPTIONS: Array<{
  value: CalendarTextSize;
  label: string;
}> = [{
  value: "small",
  label: "Small"
}, {
  value: "medium",
  label: "Medium"
}, {
  value: "large",
  label: "Large"
}, {
  value: "extra_large",
  label: "Extra large"
}];

/**
 * How event titles wrap. A word wider than the card (common at the larger
 * sizes in narrow week columns) is hyphenated, or failing that broken, inside
 * the card instead of running past its edge and being clipped. `anywhere`
 * rather than `break-word` because only it lets a shrink-to-fit box narrow
 * below its longest word.
 */
export const CALENDAR_TITLE_WRAP_CLASSNAME = "max-w-full hyphens-auto [overflow-wrap:anywhere]";

/**
 * A segment's title and time share one line until the segment is tall enough
 * for both stacked: a title line, a time line and the gap, which grows with
 * the text. Container queries cannot read CSS variables, so each size spells
 * out its own height (23px at Small). The class strings must stay
 * literal for Tailwind to generate them.
 */
const SEGMENT_HEADER_CLASSNAME: Record<CalendarTextSize, string> = {
  small: "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:23px)]:flex-col [@container_(min-height:23px)]:flex-nowrap [@container_(min-height:23px)]:items-start [@container_(min-height:23px)]:gap-px",
  medium: "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:28px)]:flex-col [@container_(min-height:28px)]:flex-nowrap [@container_(min-height:28px)]:items-start [@container_(min-height:28px)]:gap-px",
  large: "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:33px)]:flex-col [@container_(min-height:33px)]:flex-nowrap [@container_(min-height:33px)]:items-start [@container_(min-height:33px)]:gap-px",
  extra_large: "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:37px)]:flex-col [@container_(min-height:37px)]:flex-nowrap [@container_(min-height:37px)]:items-start [@container_(min-height:37px)]:gap-px"
};

/**
 * Memo gates, sized for each text size like the segment header above. A card
 * narrower than the width shows only the memo icon (41px at Small); a space
 * shorter than the height hides the memo text (13px at Small, one line).
 */
const MEMO_GATE_CLASSNAMES: Record<CalendarTextSize, {
  iconOnly: string;
  text: string;
  list: string;
}> = {
  small: {
    iconOnly: "@[41px]:hidden",
    text: "hidden @[41px]:block",
    list: "[@container_(max-height:13px)]:hidden"
  },
  medium: {
    iconOnly: "@[49px]:hidden",
    text: "hidden @[49px]:block",
    list: "[@container_(max-height:16px)]:hidden"
  },
  large: {
    iconOnly: "@[57px]:hidden",
    text: "hidden @[57px]:block",
    list: "[@container_(max-height:18px)]:hidden"
  },
  extra_large: {
    iconOnly: "@[66px]:hidden",
    text: "hidden @[66px]:block",
    list: "[@container_(max-height:21px)]:hidden"
  }
};
export function memoGateClassNames(size: CalendarTextSize) {
  return MEMO_GATE_CLASSNAMES[size];
}
export function segmentHeaderClassName(size: CalendarTextSize): string {
  return SEGMENT_HEADER_CLASSNAME[size];
}
export function calendarTextScaleStyle(size: CalendarTextSize): React.CSSProperties {
  return {
    "--calendar-font-scale": CALENDAR_TEXT_SCALE[size]
  } as React.CSSProperties;
}
const CalendarTextSizeOverrideContext = React.createContext<CalendarTextSize | null>(null);

/**
 * Draws the calendar cards inside it at a given text size instead of the
 * viewer's own, e.g. the previews beside the Calendar Text Size setting.
 */
export const CalendarTextSizeOverride = CalendarTextSizeOverrideContext.Provider;

/** The Calendar text size to draw at: the signed-in user's, unless overridden. */
export function useCalendarTextSize() {
  const override = React.useContext(CalendarTextSizeOverrideContext);
  const {
    currentUser
  } = useUser();
  return override ?? currentUser?.calendarTextSize ?? "small";
}